import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sun, Moon, ArrowRight, Lock, Compass, ChevronDown, ChevronRight,
  Target, Sliders, Plane, Laptop,
  HardHat, Users, Users2, Briefcase, Activity,
  Receipt, LayoutDashboard,
  FileBarChart, Scale, PiggyBank, PieChart, Building2,
} from "lucide-react";
import { getRoleFromToken } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { departamentosVisiveis, DEPARTAMENTOS_DESPESAS_GERAIS } from "../../constants/departamentosOrcamento";
import { ROLES_PREMISSAS_FATURAMENTO } from "../../constants/premissasFaturamento";
import { ROLES_MAO_DE_OBRA } from "../../constants/maoDeObra";
import { ROLES_PREMISSAS_VIAGEM } from "../../constants/premissasViagem";
import { ROLES_PREMISSAS_EQUIPAMENTOS } from "../../constants/premissasEquipamentos";
import { ROLES_ALOCACAO_PESSOAL } from "../../constants/alocacaoPessoal";
import { ROLES_CUSTOS_GERAIS_PROJETO } from "../../constants/custosGeraisProjeto";
import { ROLES_VISAO_EXECUTIVA } from "../../constants/visaoExecutiva";
import { ROLES_VISAO_EXECUTIVA_GERAL } from "../../constants/visaoExecutivaGeral";
import { ROLES_IMPOSTOS } from "../../constants/impostos";
import { ROLES_INVESTIMENTOS } from "../../constants/investimentos";
import { ROLES_DRE_GERENCIAL } from "../../constants/dreGerencial";
import { ROLES_ANALISE_DEPARTAMENTO } from "../../constants/analiseDepartamento";
import { ROLES_DRE_EMPRESA } from "../../constants/dreEmpresa";

// Paleta padrão da plataforma (mesma usada nas demais telas de Gestão Orçamentária)
const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  bgActive: isLight ? "#F1E9FE" : "#241A3D",
});

// ─────────────────────────────────────────────────────────────
// As 4 etapas do módulo, na mesma ordem em que aparecem como grupos no menu
// lateral (Premissas → Custos → Despesas → Resultado). Cada etapa tem seus
// próprios passos, que podem usar `roles` (lista fechada de roles) OU
// `departamentos` (liberado a quem enxerga pelo menos 1 daqueles
// departamentos) — nunca os dois — checados por temAcessoPasso.
// ─────────────────────────────────────────────────────────────
const ETAPAS = [
  {
    id: "premissas",
    numero: 1,
    icon: Target,
    titulo: "Premissas",
    resumo: "Os parâmetros que alimentam os demais módulos.",
    intro: "Diferente das etapas seguintes, as 4 Premissas abaixo não são passos sequenciais entre si — cada uma é um parâmetro independente que alimenta um módulo diferente (Faturamento → visão executiva de vendas; Mão de Obra → Custo/Despesas Mão de Obra; Viagens → \"Lançar Viagem\" em Custos Gerais por Projeto e Despesas Gerais). O acesso também varia bastante entre elas. Configure cada uma antes de usar o módulo correspondente.",
    passos: [
      {
        numero: 1,
        icon: Target,
        titulo: "Premissas de Faturamento",
        path: "/gestao-orcamentaria/premissas-faturamento",
        roles: ROLES_PREMISSAS_FATURAMENTO,
        resumo: "Defina vendas, pipeline, meta de faturamento, backlog e alíquotas/margens projetadas — visão executiva, não é por departamento.",
        pontos: [
          "Pipeline, Vendas Totais e Meta de Faturamento Bruto são editáveis; Novas Vendas e os totais líquidos são sempre calculados a partir deles.",
          "Mostra o ano corrente lado a lado com o ano anterior, com variação percentual, sem precisar trocar o seletor de ano.",
          "Independente das demais: alimenta a visão executiva de vendas/margem, sem relação com Mão de Obra, Viagens ou Equipamentos.",
        ],
      },
      {
        numero: 2,
        icon: Sliders,
        titulo: "Premissas · Mão de Obra",
        path: "/gestao-orcamentaria/premissas-mao-de-obra",
        roles: ROLES_MAO_DE_OBRA,
        resumo: "Configure os percentuais de Encargos e os valores de Benefícios usados no custo de todo colaborador, por regime (CLT/Estágio/PJ).",
        pontos: [
          "Encargos e Benefícios são globais por regime; só \"Incluir no Total\" varia por departamento — use o seletor de departamento no menu lateral.",
          "Alimenta o cálculo de custo de Custo Mão de Obra (Operação) e Despesas Mão de Obra (demais departamentos) — mude aqui antes de conferir lá.",
          "Mesmo perfil de acesso de Custo/Despesas Mão de Obra, já que também expõe parâmetros usados no salário de todo colaborador.",
        ],
      },
      {
        numero: 3,
        icon: Plane,
        titulo: "Premissas · Viagens",
        path: "/gestao-orcamentaria/premissas-viagem",
        roles: ROLES_PREMISSAS_VIAGEM,
        resumo: "Consulte o ticket médio de viagem por projeto (Operação) e o ticket único \"Backoffice\" (demais departamentos).",
        pontos: [
          "Ticket por Projeto alimenta \"Lançar Viagem\" em Custos Gerais por Projeto; o ticket Backoffice alimenta o mesmo atalho em Despesas Gerais.",
          "Diferente das outras 3 Premissas: liberada para consulta a qualquer perfil com acesso à Gestão Orçamentária — só o admin edita os valores.",
          "Editar aqui muda o valor sugerido em novos lançamentos; não recalcula lançamentos já feitos.",
        ],
      },
      {
        numero: 4,
        icon: Laptop,
        titulo: "Premissas · Equipamentos e Materiais",
        path: "/gestao-orcamentaria/premissas-equipamentos",
        roles: ROLES_PREMISSAS_EQUIPAMENTOS,
        resumo: "Ticket médio único e global de equipamentos e materiais (Fone, Mouse, Teclado, Notebook, Outros).",
        pontos: [
          "Lista fixa de 5 itens — não dá para criar nem remover categoria aqui.",
          "Só o admin edita os valores; os demais perfis com acesso apenas visualizam.",
          "Ainda não está vinculada a nenhum lançamento de custo/despesa — é uma premissa isolada, fora de escopo por ora.",
        ],
      },
    ],
  },
  {
    id: "custos",
    numero: 2,
    icon: HardHat,
    titulo: "Custos (Operação)",
    resumo: "Fluxo de 4 passos, exclusivo da Operação.",
    intro: "Cadastre o pessoal da Operação, aloque esse pessoal aos projetos, lance os demais custos por projeto e acompanhe tudo consolidado na Visão Executiva. Siga os 4 passos abaixo na ordem — cada um usa dados do anterior.",
    passos: [
      {
        numero: 1,
        icon: Users,
        titulo: "Custo Mão de Obra",
        path: "/gestao-orcamentaria/custo-mao-de-obra",
        roles: ROLES_MAO_DE_OBRA,
        resumo: "Cadastre os colaboradores da Operação e a composição de custo de cada um — salário, encargos e benefícios.",
        pontos: [
          "\"Nova linha\" cadastra um colaborador — é por aqui que entra uma contratação nova.",
          "Reajuste 1 e Reajuste 2 registram a evolução do colaborador ao longo do ano, cada um com seu mês de vigência.",
          "Encargos, benefícios e a projeção mensal de custo são calculados automaticamente a partir desses dados.",
        ],
      },
      {
        numero: 2,
        icon: Users2,
        titulo: "Alocação de Pessoal",
        path: "/gestao-orcamentaria/alocacao-pessoal",
        roles: ROLES_ALOCACAO_PESSOAL,
        resumo: "Distribua, mês a mês, o percentual de tempo de cada colaborador cadastrado no passo anterior entre os projetos.",
        pontos: [
          "Clique no colaborador para abrir a grade com todos os projetos × os 12 meses de uma vez.",
          "A soma do % alocado em cada mês não pode passar de 100%.",
          "Essa alocação alimenta o tipo de lançamento \"Alocação por Projeto\" em Custos Gerais por Projeto (próximo passo).",
        ],
      },
      {
        numero: 3,
        icon: Briefcase,
        titulo: "Custos Gerais por Projeto",
        path: "/gestao-orcamentaria/custos-gerais-projeto",
        roles: ROLES_CUSTOS_GERAIS_PROJETO,
        resumo: "Lance, projeto a projeto, os demais custos (viagens, softwares, serviços...) pelos botões de categoria.",
        pontos: [
          "Abra o projeto e clique no botão da categoria (ex: Despesas de Viagens) para escolher a subcategoria e lançar o item.",
          "Despesas de Viagens tem o atalho \"Lançar Viagem\", que usa o ticket médio travado do projeto.",
          "Cada item pode ter mais de um período de vigência, quando o valor muda ao longo do ano.",
        ],
      },
      {
        numero: 4,
        icon: Activity,
        titulo: "Visão Executiva",
        path: "/gestao-orcamentaria/visao-executiva",
        roles: ROLES_VISAO_EXECUTIVA,
        resumo: "Acompanhe o resultado dos 3 passos anteriores: saúde da alocação, ranking de custo por projeto e ociosidade acumulada.",
        pontos: [
          "Mostra quem está sub ou sobre-alocado em cada mês.",
          "Ranking de custo de pessoal por projeto, cruzando Custo Mão de Obra × Alocação de Pessoal.",
          "Ociosidade acumulada — tempo de colaboradores não alocado a nenhum projeto.",
        ],
      },
    ],
  },
  {
    id: "despesas",
    numero: 3,
    icon: Receipt,
    titulo: "Despesas (demais departamentos)",
    resumo: "Fluxo equivalente ao de Custos, fora da Operação.",
    intro: "É mais curto que o fluxo de Custos: não existe Alocação de Pessoal nem separação por projeto — a despesa é lançada direto no departamento. O acesso também é misto: Despesas Mão de Obra e Visão Executiva Despesas expõem salário e ficam restritos por role, enquanto Despesas Gerais é liberado por departamento.",
    passos: [
      {
        numero: 1,
        icon: Users,
        titulo: "Despesas Mão de Obra",
        path: "/gestao-orcamentaria/despesas-mao-de-obra",
        roles: ROLES_MAO_DE_OBRA,
        resumo: "Cadastre os colaboradores dos demais departamentos (fora da Operação) e a composição de custo de cada um — salário, encargos e benefícios.",
        pontos: [
          "\"Nova linha\" cadastra um colaborador de qualquer departamento — é por aqui que entra uma contratação nova.",
          "Reajuste 1 e Reajuste 2 registram a evolução do colaborador ao longo do ano, mesma lógica do Custo Mão de Obra da Operação.",
          "Diferente do Custo Mão de Obra (exclusivo da Operação), aqui todos os departamentos aparecem juntos — use os filtros de coluna para focar no seu.",
        ],
      },
      {
        numero: 2,
        icon: Receipt,
        titulo: "Despesas Gerais",
        path: "/gestao-orcamentaria/despesas-gerais",
        departamentos: DEPARTAMENTOS_DESPESAS_GERAIS,
        resumo: "Lance as demais despesas do seu departamento (viagens, softwares, serviços...) pelos botões de categoria.",
        pontos: [
          "Se você tem acesso a mais de um departamento, o menu lateral mostra um seletor — escolha o departamento antes de lançar.",
          "\"Lançar Viagem\" usa um ticket médio único (\"Backoffice\"), compartilhado por todos os departamentos.",
          "Não existe Alocação de Pessoal nem separação por projeto: a despesa entra direto no departamento.",
        ],
      },
      {
        numero: 3,
        icon: LayoutDashboard,
        titulo: "Visão Executiva Despesas",
        path: "/gestao-orcamentaria/visao-executiva-despesas",
        roles: ROLES_VISAO_EXECUTIVA_GERAL,
        resumo: "Acompanhe o resultado dos 2 passos anteriores: indicadores de mão de obra e concentração de gastos por categoria mãe de todos os departamentos.",
        pontos: [
          "Headcount, mix PJ/CLT, salário médio e reajustes agregados de todos os departamentos (fora da Operação).",
          "Concentração de gastos por categoria mãe, cruzando Despesas Mão de Obra × Despesas Gerais.",
          "Mesma lógica da Visão Executiva Custos, só que para os departamentos de Despesas.",
        ],
      },
    ],
  },
  {
    id: "resultado",
    numero: 4,
    icon: FileBarChart,
    titulo: "Resultado",
    resumo: "Onde Premissas, Custos e Despesas se encontram.",
    intro: "Diferente das 3 etapas anteriores, este grupo não é um fluxo sequencial: são painéis de acompanhamento e apuração — a maioria somente leitura — que você consulta conforme a necessidade, depois que já lançou os dados nas etapas anteriores.",
    passos: [
      {
        numero: 1,
        icon: Activity,
        titulo: "Visão Executiva",
        path: "/gestao-orcamentaria/visao-executiva",
        roles: ROLES_VISAO_EXECUTIVA,
        resumo: "Fechamento do fluxo de Custos: saúde da alocação, ranking de custo por projeto e ociosidade acumulada da Operação.",
        pontos: [
          "Mostra quem está sub ou sobre-alocado em cada mês.",
          "Ranking de custo de pessoal por projeto, cruzando Custo Mão de Obra × Alocação de Pessoal.",
        ],
      },
      {
        numero: 2,
        icon: Scale,
        titulo: "Impostos",
        path: "/gestao-orcamentaria/impostos",
        roles: ROLES_IMPOSTOS,
        resumo: "Consolida os impostos sobre o resultado — IRPJ, CSLL e demais tributos — em visão geral, sem separação por departamento.",
        pontos: [
          "As linhas de IRPJ e CSLL são calculadas automaticamente pela mesma cascata usada em DRE por Empresa (com compensação de prejuízo fiscal) — você não edita esses valores diretamente aqui.",
          "Os demais tributos e taxas são lançados manualmente, com categoria/subcategoria e vigência, igual às demais telas do módulo.",
          "Alimenta o LAIR/resultado final no DRE Gerencial e no DRE por Empresa.",
        ],
      },
      {
        numero: 3,
        icon: PiggyBank,
        titulo: "Investimentos",
        path: "/gestao-orcamentaria/investimentos",
        roles: ROLES_INVESTIMENTOS,
        resumo: "Lançamentos de investimentos em terceiros, imobilizados, YouXLab e equipamentos — visão geral, sem separação por departamento.",
        pontos: [
          "Mesmo modelo de categoria/subcategoria com vigência usado em Despesas Gerais e Despesas Financeiras.",
          "Entra no resultado consolidado do DRE Gerencial e, via rateio, no DRE por Empresa.",
        ],
      },
      {
        numero: 4,
        icon: FileBarChart,
        titulo: "DRE Gerencial",
        path: "/gestao-orcamentaria/dre-gerencial",
        roles: ROLES_DRE_GERENCIAL,
        resumo: "O consolidado de tudo: Faturamento, Custos, Despesas, Impostos e Investimentos organizados como uma demonstração de resultado (Faturamento Bruto → Receita Líquida → Lucro Bruto → EBITDA → LAIR). Somente leitura.",
        pontos: [
          "Reúne automaticamente o que foi lançado em cada etapa anterior — não tem lançamento próprio.",
          "É a referência para checar se os números dos demais módulos estão fechando.",
        ],
      },
      {
        numero: 5,
        icon: PieChart,
        titulo: "Análise por Departamento",
        path: "/gestao-orcamentaria/analise-departamento",
        roles: ROLES_ANALISE_DEPARTAMENTO,
        resumo: "Ranking de custos por departamento, com detalhamento por plano de contas. Somente leitura.",
        pontos: [
          "Cruza os lançamentos de Custos Gerais, Despesas Gerais e Mão de Obra de todos os departamentos.",
          "Útil para achar rapidamente onde está concentrado o gasto antes de ajustar premissas ou lançamentos.",
        ],
      },
      {
        numero: 6,
        icon: Building2,
        titulo: "DRE por Empresa",
        path: "/gestao-orcamentaria/dre-empresa",
        roles: ROLES_DRE_EMPRESA,
        resumo: "O mesmo DRE Gerencial, só que aberto por empresa: GT, CIGTA e CRIA. Somente leitura.",
        pontos: [
          "Faturamento Bruto é dado real por empresa; os demais custos/despesas usam um rateio percentual configurável por categoria/ano (precisa somar 100% entre as 3 empresas).",
          "Impostos (IRPJ + CSLL) são calculados automaticamente por empresa, mês a mês, com compensação de prejuízo fiscal acumulado.",
        ],
      },
    ],
  },
];

// Passos com `roles` checam a lista diretamente; passos com `departamentos`
// (ex: Despesas Gerais) seguem a mesma regra do menu lateral — liberado para
// quem enxerga pelo menos 1 dos departamentos do passo.
function temAcessoPasso(passo, role) {
  if (passo.departamentos) return departamentosVisiveis(role).some((d) => passo.departamentos.includes(d));
  return passo.roles.includes(role);
}

export default function BoasVindasGestaoOrcamentariaView() {
  const [isLight, setIsLight] = useState(true);
  const [etapaAberta, setEtapaAberta] = useState("premissas");
  const C = palette(isLight);
  const navigate = useNavigate();
  const role = getRoleFromToken();

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Boas-vindas</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 12px",
              cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={{
              display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 14px",
              cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600,
            }}>
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24, width: "100%", maxWidth: 900, margin: "0 auto" }}>
          {/* Hero */}
          <section style={{
            display: "flex", alignItems: "flex-start", gap: 16,
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "22px 24px",
          }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              width: 46, height: 46, borderRadius: 12, background: `${C.brand}1F`, color: C.brand,
            }}>
              <Compass size={24} />
            </div>
            <div>
              <h1 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 800, color: C.text }}>Bem-vindo à Gestão Orçamentária</h1>
              <p style={{ margin: 0, fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                O módulo segue a lógica clássica de planejamento financeiro: primeiro você define as{" "}
                <strong style={{ color: C.text }}>Premissas</strong> (os parâmetros que alimentam tudo), depois
                lança <strong style={{ color: C.text }}>Custos</strong> (Operação) e{" "}
                <strong style={{ color: C.text }}>Despesas</strong> (demais departamentos) mês a mês, e por fim
                acompanha o <strong style={{ color: C.text }}>Resultado</strong> consolidado — impostos,
                investimentos e as visões de DRE. Essas 4 etapas aparecem no menu lateral na mesma ordem, como
                grupos separados. Clique em cada etapa abaixo para ver o passo a passo detalhado, ou vá direto
                pelo menu lateral quando já souber para onde ir.
              </p>
            </div>
          </section>

          {/* Etapas (acordeão) */}
          <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {ETAPAS.map((etapa) => {
              const EtapaIcon = etapa.icon;
              const aberta = etapaAberta === etapa.id;
              return (
                <div key={etapa.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
                  <button
                    onClick={() => setEtapaAberta(aberta ? null : etapa.id)}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", gap: 14,
                      padding: "16px 20px", background: aberta ? C.bgActive : "transparent", border: "none",
                      cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                    }}
                  >
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      width: 34, height: 34, borderRadius: "50%", background: C.brand, color: "#fff",
                      fontSize: 14, fontWeight: 800,
                    }}>
                      {etapa.numero}
                    </div>
                    <EtapaIcon size={18} color={C.brand} style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{etapa.titulo}</div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{etapa.resumo}</div>
                    </div>
                    {aberta ? <ChevronDown size={18} color={C.muted} /> : <ChevronRight size={18} color={C.muted} />}
                  </button>

                  {aberta && (
                    <div style={{ padding: "4px 20px 22px" }}>
                      <p style={{ margin: "0 0 18px", fontSize: 13, color: C.muted, lineHeight: 1.6 }}>{etapa.intro}</p>

                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {etapa.passos.map((passo, idx) => {
                          const Icon = passo.icon;
                          const ultimo = idx === etapa.passos.length - 1;
                          const temAcesso = temAcessoPasso(passo, role);
                          return (
                            <div key={passo.numero} style={{ display: "flex", gap: 18 }}>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 30, flexShrink: 0 }}>
                                <div style={{
                                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                  width: 30, height: 30, borderRadius: "50%", background: C.bg, border: `1.5px solid ${C.brand}`,
                                  color: C.brand, fontSize: 13, fontWeight: 800,
                                }}>
                                  {passo.numero}
                                </div>
                                {!ultimo && <div style={{ flex: 1, width: 2, background: C.border, marginTop: 6, marginBottom: 6, minHeight: 20 }} />}
                              </div>

                              <div style={{ flex: 1, paddingBottom: ultimo ? 0 : 18, minWidth: 0 }}>
                                <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <Icon size={16} color={C.brand} />
                                    <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: C.text }}>{passo.titulo}</h3>
                                  </div>
                                  <p style={{ margin: 0, fontSize: 12.5, color: C.text, lineHeight: 1.5 }}>{passo.resumo}</p>
                                  <ul style={{ margin: 0, padding: "0 0 0 16px", display: "flex", flexDirection: "column", gap: 3 }}>
                                    {passo.pontos.map((ponto, i) => (
                                      <li key={i} style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>{ponto}</li>
                                    ))}
                                  </ul>
                                  {temAcesso ? (
                                    <button
                                      onClick={() => navigate(passo.path)}
                                      style={{
                                        alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6,
                                        background: "transparent", border: `1px solid ${C.brand}`, borderRadius: 8,
                                        padding: "7px 12px", color: C.brand, fontSize: 11.5, fontWeight: 700,
                                        cursor: "pointer", fontFamily: "inherit", marginTop: 2,
                                      }}
                                    >
                                      Ir para {passo.titulo} <ArrowRight size={12} />
                                    </button>
                                  ) : (
                                    <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.muted, marginTop: 2 }}>
                                      <Lock size={11} /> Sem acesso a esta tela com o seu perfil atual
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        </main>
      </div>
    </div>
  );
}
